import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { bannersService } from '../../lib/api/shopService';
import { getImageUrl } from '../../utils/apiHelpers';
import { BANNER_TYPE, BANNER_SECTION } from '../../types';
import {
  getBannerId,
  isBannerVisible,
  normalizeBannersList,
} from '../../utils/bannerHelpers';

const SLIDE_INTERVAL_MS = 5000;
const COUNTDOWN_END_HOURS = 24;
const SWIPE_THRESHOLD_PX = 50;

const PromotionalSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const lastSwipeTime = useRef(0);

  const { data: bannersData, isLoading, isError } = useQuery({
    queryKey: ['banners', BANNER_TYPE.BANNIERE],
    queryFn: () =>
      bannersService.getBanners({
        type: BANNER_TYPE.BANNIERE,
        visibleOnly: true,
      }),
    enabled: true,
  });

  const bannerList = useMemo(
    () => normalizeBannersList(bannersData),
    [bannersData],
  );

  const sliderBanners = useMemo(
    () =>
      bannerList.filter(
        (b) =>
          b.section === BANNER_SECTION.SECTION_1 && isBannerVisible(b),
      ),
    [bannerList]
  );

  const specialOffer = useMemo(
    () =>
      bannerList.find(
        (b) => b.section === BANNER_SECTION.SECTION_2 && isBannerVisible(b),
      ),
    [bannerList]
  );

  const slideCount = sliderBanners.length;
  const currentIndex = slideCount > 0 ? ((currentSlide % slideCount) + slideCount) % slideCount : 0;

  const goToSlide = useCallback(
    (index) => {
      if (slideCount === 0) return;
      const next = ((index % slideCount) + slideCount) % slideCount;
      setCurrentSlide(next);
    },
    [slideCount]
  );

  const nextSlide = useCallback(
    (e) => {
      e?.preventDefault();
      if (slideCount === 0) return;
      setCurrentSlide((prev) => prev + 1);
    },
    [slideCount]
  );

  const prevSlide = useCallback(
    (e) => {
      e?.preventDefault();
      if (slideCount === 0) return;
      setCurrentSlide((prev) => prev - 1);
    },
    [slideCount]
  );

  // Réinitialiser l'index si la liste change (ex: refetch avec moins de slides)
  useEffect(() => {
    if (slideCount > 0 && currentSlide >= slideCount) {
      setCurrentSlide(0);
    }
  }, [slideCount, currentSlide]);

  // Auto-slide (pause quand l'utilisateur survole ou interagit)
  useEffect(() => {
    if (slideCount <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => prev + 1);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [slideCount, isPaused]);

  // Countdown: 24h from now
  useEffect(() => {
    const endTime = new Date(Date.now() + COUNTDOWN_END_HOURS * 60 * 60 * 1000);

    const tick = () => {
      const diff = endTime - new Date();
      if (diff <= 0) return;
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSwipe = useCallback(() => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) < SWIPE_THRESHOLD_PX) return;
    lastSwipeTime.current = Date.now();
    if (diff > 0) nextSlide({ preventDefault: () => {} });
    else prevSlide({ preventDefault: () => {} });
  }, [nextSlide, prevSlide]);

  const handleSlideClick = useCallback((e) => {
    if (Date.now() - lastSwipeTime.current < 400) {
      e.preventDefault();
    }
  }, []);

  const onTouchStart = (e) => {
    touchStartX.current = e.targetTouches?.[0]?.clientX ?? 0;
  };
  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches?.[0]?.clientX ?? 0;
  };
  const onTouchEnd = () => {
    handleSwipe();
  };

  if (isLoading) {
    return (
      <div className="w-full h-[220px] lg:h-[320px] mb-4 rounded-xl bg-gray-200 animate-pulse flex items-center justify-center">
        <span className="text-gray-500 text-sm">Chargement du slider…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="w-full h-[220px] lg:h-[320px] mb-4 rounded-xl bg-gray-100 flex items-center justify-center">
        <span className="text-gray-500 text-sm">Impossible de charger le slider.</span>
      </div>
    );
  }

  const hasSlides = slideCount > 0;
  const hasOffer = Boolean(specialOffer?.imagePath);

  return (
    <div className="w-full mb-4">
      <div className="flex flex-col lg:flex-row gap-4 px-4 lg:px-0">
        {/* Slider principal */}
        <div
          className="w-full lg:w-3/5 relative overflow-hidden rounded-xl bg-gray-900 select-none h-[220px] lg:h-[500px]"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocus={() => setIsPaused(true)}
          onBlur={() => setIsPaused(false)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {hasSlides ? (
            <>
              <div
                className="flex transition-transform duration-500 ease-out h-full min-h-0"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {sliderBanners.map((slide, index) => (
                  <a
                    key={getBannerId(slide)}
                    href={slide.link || '#'}
                    className="w-full flex-shrink-0 block h-full"
                    target={slide.link ? '_blank' : undefined}
                    rel={slide.link ? 'noopener noreferrer' : undefined}
                    onClick={handleSlideClick}
                  >
                    <img
                      className="w-full h-full object-cover"
                      src={getImageUrl(slide.imagePath)}
                      alt={slide.title}
                      loading={index === 0 ? undefined : 'lazy'}
                      decoding="async"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="%23374151" width="400" height="300"/><text fill="%239ca3af" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="14">Image indisponible</text></svg>';
                      }}
                    />
                  </a>
                ))}
              </div>

              {slideCount > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevSlide}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
                    aria-label="Slide précédent"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={nextSlide}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
                    aria-label="Slide suivant"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {sliderBanners.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => goToSlide(index)}
                        className={`h-2 rounded-full transition-all ${
                          index === currentIndex ? 'w-6 bg-white' : 'w-2 bg-white/50 hover:bg-white/70'
                        }`}
                        aria-label={`Aller au slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="min-h-[220px] lg:min-h-[320px] flex items-center justify-center text-gray-500 text-sm">
              Aucune bannière pour le moment
            </div>
          )}
        </div>

        {/* Bloc offre / section droite — même hauteur que le slider */}
        <div className="w-full lg:w-2/5 flex flex-col gap-2">
          {hasOffer ? (
            <a
              href={specialOffer.link || '#'}
              className="block relative overflow-hidden rounded-xl bg-[#F7CC60] h-[220px] lg:h-[500px]"
              target={specialOffer.link ? '_blank' : undefined}
              rel={specialOffer.link ? 'noopener noreferrer' : undefined}
            >
              <img
                className="w-full h-full object-cover object-center"
                src={getImageUrl(specialOffer.imagePath)}
                alt={specialOffer.title}
                decoding="async"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
            </a>
          ) : (
            <div className="bg-[#F7CC60] h-[220px] lg:h-[500px] rounded-xl flex flex-col items-center justify-center overflow-hidden">
              <img className="w-full h-full object-cover rounded-xl" src="/images/special-offer.png" alt="" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromotionalSlider;
